import { useState, useEffect, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import Modal from '@/components/layout/Modal'
import { showToast } from '@/components/layout/Toast'
import type { Contacto, ContactoEmpresa, Cliente, RolContacto } from '@/lib/types'
import { ROLES_CONTACTO } from '@/lib/types'
import { PageHeader, RelationCell, Pagination } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import type { RelationItem } from '@/components/ui'
import { IconPlus, IconBuilding } from '@/components/icons/Icons'

/** Sin caracteres ambiguos (0/O, 1/l/I) porque se dicta o se copia a mano. */
function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let pw = ''
  for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)]
  return pw
}

// ─── Form types ───
interface ContactoForm {
  nombre: string
  apellido: string
  email: string
  telefono: string
  cargo: string
  notas: string
}

interface AfilForm {
  cliente_id: number | null
  roles: RolContacto[]
  cargo_empresa: string
  es_principal: boolean
}

const emptyContacto: ContactoForm = {
  nombre: '', apellido: '', email: '', telefono: '', cargo: '', notas: '',
}

const emptyAfil = (): AfilForm => ({
  cliente_id: null, roles: [], cargo_empresa: '', es_principal: false,
})

// ─── Helpers ───

async function loadAfiliaciones(api: ReturnType<typeof useApi>, contactoId: number, clientes: Cliente[]): Promise<(ContactoEmpresa & { empresa_nombre: string })[]> {
  const data = await api.get<any[]>(`/api/contactos/${contactoId}/empresas`)
  return data.map((ce: any) => ({
    ...ce,
    empresa_nombre: clientes.find(c => c.id === ce.cliente_id)?.empresa ?? '—',
  }))
}

const rolBadgeColors: Record<string, string> = {
  'PRINCIPAL': '#00d2d3', 'FACTURACIÓN': '#f39c12', 'TÉCNICO': '#3498db', 'COMERCIAL': '#2ecc71', 'GERENCIA': '#9b59b6',
}

export default function Contactos() {
  const navigate = useNavigate()
  // La pagina se monta tanto en el ERP como en el espacio partner.
  const enPartner = useLocation().pathname.startsWith('/partner')
  const { isAdmin } = useAuth()
  /** Quien reparte accesos del ERP. Ni el partner ni una sesión recortada. */
  const esAdminDelErp = isAdmin && !enPartner
  const api = useApi()
  const { data: contactos = [], isLoading: loading } = useCachedQuery<Contacto>('contactos')
  const { data: clientes = [] } = useCachedQuery<Cliente>('clientes')
  const { addMut, updateMut, removeMut, invalidate } = useCachedMutations('contactos')

  const [modalOpen, setModalOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<Contacto | null>(null)
  const [editing, setEditing] = useState<Contacto | null>(null)
  const [form, setForm] = useState<ContactoForm>(emptyContacto)
  const [afiliaciones, setAfiliaciones] = useState<AfilForm[]>([])
  const [existingAfils, setExistingAfils] = useState<ContactoEmpresa[]>([])
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [creatingPortal, setCreatingPortal] = useState<number | null>(null)
  // Credenciales recien generadas, para que el admin pueda copiarlas.
  const [nuevoAcceso, setNuevoAcceso] = useState<{ email: string; password: string } | null>(null)
  // Emails que ya tienen identidad en `usuarios`. La columna Portal se basa en
  // esto y no en contactos.portal_activo, que quedo desactualizado tras la
  // migracion desde Supabase Auth: hay contactos marcados como activos cuyo
  // usuario no existe, y por tanto no pueden entrar.
  const [emailsConCuenta, setEmailsConCuenta] = useState<Set<string> | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  /*
    Las empresas de cada contacto. Un contacto es un directorio M:N —la misma
    persona puede llevar dos empresas— y hasta ahora la tabla no decia ninguna:
    habia que abrir la ficha para saber de donde era. Sale de la misma consulta
    que alimenta la tabla de Empresas, asi que es una sola ida al servidor.
  */
  type EmpresaVinculo = { cliente_id: number; nombre: string; cargo: string | null; roles: string[]; es_principal: boolean }
  const [empresasPorContacto, setEmpresasPorContacto] = useState<Record<number, EmpresaVinculo[]>>({})

  // Las fichas viven en rutas distintas segun el espacio que monte la pagina.
  const rutaContactos = enPartner ? '/partner/contactos' : '/clientes/contactos'
  const rutaEmpresas = enPartner ? '/partner/empresas' : '/clientes'

  useEffect(() => {
    if (clientes.length === 0) return
    api.get<any[]>('/api/contactos/with-empresas')
      .then(filas => {
        const mapa: Record<number, EmpresaVinculo[]> = {}
        for (const fila of filas) {
          const v = fila.contacto_empresa
          const contactoId = v?.contacto_id
          if (!v || !contactoId) continue
          // Sin nombre no hay nada que mostrar: es una empresa que este
          // usuario no ve, y nombrarla seria filtrarla.
          const nombre = clientes.find(c => c.id === v.cliente_id)?.empresa
          if (!nombre) continue
          const lista = mapa[contactoId] ?? (mapa[contactoId] = [])
          lista.push({
            cliente_id: v.cliente_id,
            nombre,
            cargo: v.cargo_empresa ?? null,
            roles: v.roles ?? [],
            es_principal: !!v.es_principal,
          })
        }
        for (const lista of Object.values(mapa)) {
          lista.sort((a, b) => Number(b.es_principal) - Number(a.es_principal))
        }
        setEmpresasPorContacto(mapa)
      })
      .catch(err => console.error('Error cargando las empresas de los contactos:', err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes, contactos])

  const itemsEmpresa = (ct: Contacto): RelationItem[] =>
    (empresasPorContacto[ct.id] ?? []).map(e => ({
      id: e.cliente_id,
      label: e.nombre,
      sub: e.cargo,
      badges: e.roles,
      to: `${rutaEmpresas}/${e.cliente_id}`,
      principal: e.es_principal,
    }))

  // Auto-open edit modal from query param (e.g. ?edit=3)
  useEffect(() => {
    // /api/auth/usuarios es solo-admin; en el espacio partner ni se intenta.
    // Lo mismo para una sesión recortada: la columna Portal es administración
    // de accesos, y pedirla dejaba un 403 por visita para pintar algo que esa
    // persona no puede tocar igual.
    if (enPartner || !esAdminDelErp) { setEmailsConCuenta(new Set()); return }
    api.get<{ email: string }[]>('/api/auth/usuarios')
      .then(us => setEmailsConCuenta(new Set(us.map(u => u.email.trim().toLowerCase()))))
      .catch(() => setEmailsConCuenta(new Set()))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enPartner, esAdminDelErp])

  useEffect(() => {
    const editId = searchParams.get('edit')
    if (editId && contactos.length > 0 && clientes.length > 0) {
      const ct = contactos.find(c => c.id === Number(editId))
      if (ct) {
        openEdit(ct)
        setSearchParams({}, { replace: true })
      }
    }
  }, [searchParams, contactos, clientes])

  // ─── Portal account helpers ───
  async function loadContactoAfils(contactoId: number): Promise<{cliente_id: number; roles: RolContacto[]}[]> {
    const data = await api.get<any[]>(`/api/contactos/${contactoId}/empresas`)
    return data.map((ce: any) => ({ cliente_id: ce.cliente_id, roles: ce.roles }))
  }

  const tieneCuenta = (ct: Contacto): boolean =>
    !!ct.email && !!emailsConCuenta?.has(ct.email.trim().toLowerCase())

  /**
   * Un partner ya recibe servicios por su propio espacio, asi que no necesita
   * ademas un acceso de cliente: el rol partner contiene al de cliente. Por eso
   * basta con que el email ya tenga identidad para no ofrecer el alta.
   */
  function canCreatePortal(ct: Contacto): boolean {
    // El alta usa `/api/auth/register`, que es solo-admin: en el espacio del
    // partner el boton salia igual y terminaba en un 403. Dar acceso al portal
    // crea una identidad en `usuarios`, y eso lo damos nosotros.
    if (!esAdminDelErp) return false
    if (!ct.email) return false
    if (emailsConCuenta === null) return false // aun cargando: no ofrecer nada
    return !tieneCuenta(ct)
  }

  async function handleCreatePortalAccount(ct: Contacto) {
    if (!ct.email) { showToast('El contacto necesita un email para crear cuenta de portal', 'error'); return }
    setCreatingPortal(ct.id)
    try {
      // Find a client affiliation for this contact
      const afils = await loadContactoAfils(ct.id)
      const primaryAfil = afils.find(a => a.roles.includes('PRINCIPAL') || a.roles.includes('FACTURACIÓN'))
      const clienteId = primaryAfil?.cliente_id || afils[0]?.cliente_id
      if (!clienteId) { showToast('El contacto debe estar afiliado a una empresa', 'error'); return }

      // /api/auth/register exige password y no envia correo: se genera aqui y
      // se muestra al admin para que la comparta, igual que en NuevoPartner.
      const password = generatePassword()

      await api.post('/api/auth/register', {
        email: ct.email,
        password,
        role: 'cliente',
        contacto_id: ct.id,
        cliente_id: clienteId,
      })

      setNuevoAcceso({ email: ct.email, password })
      setEmailsConCuenta(prev => new Set([...(prev ?? []), ct.email!.trim().toLowerCase()]))
      showToast('Cuenta de portal creada', 'success')
      invalidate()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al crear cuenta', 'error')
    } finally {
      setCreatingPortal(null)
    }
  }

  // ─── Open modals ───
  /** El alta vive en su propia vista por pasos; el modal queda para editar. */
  function openNew() {
    navigate(enPartner ? '/partner/contactos/nuevo' : '/clientes/contactos/nuevo')
  }

  async function openEdit(ct: Contacto) {
    setEditing(ct)
    setForm({
      nombre: ct.nombre,
      apellido: ct.apellido ?? '',
      email: ct.email ?? '',
      telefono: ct.telefono ?? '',
      cargo: ct.cargo ?? '',
      notas: ct.notas ?? '',
    })
    const afils = await loadAfiliaciones(api, ct.id, clientes)
    setExistingAfils(afils)
    setAfiliaciones(afils.map(a => ({
      cliente_id: a.cliente_id,
      roles: a.roles,
      cargo_empresa: a.cargo_empresa ?? '',
      es_principal: a.es_principal,
    })))
    if (afils.length === 0) setAfiliaciones([emptyAfil()])
    setModalOpen(true)
  }



  // ─── Afiliación helpers ───
  function addAfil() { setAfiliaciones(prev => [...prev, emptyAfil()]) }
  function removeAfil(idx: number) { setAfiliaciones(prev => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)) }
  function updateAfil(idx: number, field: keyof AfilForm, value: unknown) {
    setAfiliaciones(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a))
  }
  function toggleRole(idx: number, role: RolContacto) {
    setAfiliaciones(prev => prev.map((a, i) => {
      if (i !== idx) return a
      const has = a.roles.includes(role)
      return { ...a, roles: has ? a.roles.filter(r => r !== role) : [...a.roles, role] }
    }))
  }

  // ─── Submit ───
  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.nombre.trim()) { showToast('El nombre es requerido', 'error'); return }

    // Validate afiliaciones
    const validAfils = afiliaciones.filter(a => a.cliente_id !== null)
    for (const a of validAfils) {
      if (a.roles.length === 0) {
        const emp = clientes.find(c => c.id === a.cliente_id)?.empresa ?? ''
        showToast(`Selecciona al menos un rol para la empresa "${emp}"`, 'error')
        return
      }
    }

    setSaving(true)
    try {
      let contactoId: number
      const contactoData: Record<string, unknown> = {
        nombre: form.nombre.trim(),
        apellido: form.apellido.trim() || null,
        email: form.email.trim() || null,
        telefono: form.telefono.trim() || null,
        cargo: form.cargo.trim() || null,
        notas: form.notas.trim() || null,
      }

      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, ...contactoData })
        contactoId = editing.id

        // Sync afiliaciones: delete removed, upsert existing/new
        const formClienteIds = validAfils.map(a => a.cliente_id!)
        // Delete afils that were removed
        for (const ea of existingAfils) {
          if (!formClienteIds.includes(ea.cliente_id)) {
            await api.del(`/api/contactos/junction/${ea.id}`)
          }
        }
        // Upsert
        for (const af of validAfils) {
          const existing = existingAfils.find(ea => ea.cliente_id === af.cliente_id)
          if (existing) {
            await api.put(`/api/contactos/junction/${existing.id}`, {
              contacto_id: contactoId,
              cliente_id: af.cliente_id!,
              roles: af.roles,
              cargo_empresa: af.cargo_empresa || null,
              es_principal: af.es_principal,
            })
          } else {
            await api.post(`/api/contactos/${contactoId}/empresas`, {
              cliente_id: af.cliente_id!,
              roles: af.roles,
              cargo_empresa: af.cargo_empresa || null,
              es_principal: af.es_principal,
            })
          }
        }
        showToast('Contacto actualizado', 'success')
      } else {
        contactoId = await addMut.mutateAsync(contactoData) as number
        // Create afiliaciones
        for (const af of validAfils) {
          await api.post(`/api/contactos/${contactoId}/empresas`, {
            cliente_id: af.cliente_id!,
            roles: af.roles,
            cargo_empresa: af.cargo_empresa || null,
            es_principal: af.es_principal,
          })
        }
        showToast('Contacto creado', 'success')
      }
      setModalOpen(false)
    } catch { showToast('Error al guardar contacto', 'error') }
    finally { setSaving(false) }
  }

  async function handleDelete() {
    if (!deleteConfirm) return
    try {
      // If contact has an auth user, delete from Auth first
      if (deleteConfirm.auth_user_id) {
        try {
          await api.del(`/api/auth/users/${deleteConfirm.auth_user_id}`)
        } catch (err) {
          console.warn('Auth user cleanup failed:', err)
        }
      }
      await removeMut.mutateAsync(deleteConfirm.id) // CASCADE deletes afiliaciones
      showToast('Contacto eliminado', 'success')
      setDeleteConfirm(null)
    } catch { showToast('Error al eliminar', 'error') }
  }

  // ─── Filtering ───
  const filtered = contactos.filter(c => {
    const empresas = (empresasPorContacto[c.id] ?? []).map(e => e.nombre)
    return [c.nombre, c.apellido, c.email, c.telefono, c.cargo, ...empresas]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  })

  /* De a 15, como el resto de los listados. La firma del buscador es el
     `resetKey`: al filtrar vuelve a la página 1, para no quedar mirando la
     página 7 de una lista de 2.

     Va ACÁ arriba, antes del `if (loading)`: debajo de un return temprano
     React cuenta un hook de más en el segundo render y la pantalla queda en
     blanco. */
  const { page, setPage, totalPages, paginated, pageSize, total } =
    usePagination(filtered, 15, search)

  if (loading) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <PageHeader breadcrumbs={[{ label: 'Clientes' }, { label: 'Contactos' }]} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando contactos...
      </div>
    </div>
  )

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'Clientes' }, { label: 'Contactos' }]}
        meta={`${contactos.length} registrado${contactos.length !== 1 ? 's' : ''}`}
        actions={
          <button className="btn btn-primary" onClick={openNew} id="btn-new-contact">
            <IconPlus size={16} /> Nuevo Contacto
          </button>
        }
      />

      {contactos.length > 4 && (
        <div style={{ marginBottom: '16px' }}>
          <input className="form-input" placeholder="Buscar por nombre, empresa, email, teléfono o cargo..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 400 }} />
        </div>
      )}

      {contactos.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <h3>Sin contactos</h3><p>Crea tu primer contacto para comenzar a gestionar tus relaciones.</p>
          <button className="btn btn-primary" onClick={openNew}>Crear Contacto</button>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead><tr><th>Nombre</th><th>Empresa</th><th>Email</th><th>Teléfono</th><th>Cargo</th>{!enPartner && <th>Portal</th>}<th style={{ width: 150 }}>Acciones</th></tr></thead>
            <tbody>
              {paginated.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600 }}>
                    <Link to={`${rutaContactos}/${c.id}`} className="cell-enlace">{c.nombre} {c.apellido ?? ''}</Link>
                  </td>
                  <td>
                    <RelationCell
                      items={itemsEmpresa(c)}
                      titulo={`Empresas de ${c.nombre}`}
                      nombre={['empresa', 'empresas']}
                      icono={<IconBuilding size={13} />}
                      vacio="Sin empresa"
                    />
                  </td>
                  <td>{c.email ?? '—'}</td>
                  <td>{c.telefono ?? '—'}</td>
                  <td>{c.cargo ?? '—'}</td>
                  {!enPartner && <td>
                    {tieneCuenta(c) ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem',
                        fontWeight: 600, background: 'color-mix(in srgb, var(--success) 15%, transparent)', color: 'var(--success)',
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                        Activo
                      </span>
                    ) : canCreatePortal(c) ? (
                      <button
                        className="btn btn-sm"
                        style={{
                          fontSize: '0.72rem', padding: '3px 10px',
                          background: 'color-mix(in srgb, var(--rol-principal) 10%, transparent)', color: 'var(--info)',
                          border: '1px solid color-mix(in srgb, var(--rol-principal) 30%, transparent)',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                        disabled={creatingPortal === c.id}
                        onClick={() => handleCreatePortalAccount(c)}
                      >
                        {creatingPortal === c.id ? (
                          <div className="auth-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                        )}
                        Crear Acceso
                      </button>
                    ) : (
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>}
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(
                        <button className="btn btn-sm btn-secondary" onClick={() => navigate(`${rutaContactos}/${c.id}`)} title="Ver Ficha">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        </button>
                      )}
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(c)} title="Editar">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(c)} title="Eliminar">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={enPartner ? 6 : 7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>No se encontraron resultados</td></tr>}
            </tbody>
          </table>
          <Pagination
            page={page} totalPages={totalPages} total={total} pageSize={pageSize}
            onPage={setPage} noun="contactos"
          />
        </div>
      )}

      {/* ─── Create / Edit Modal ─── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar Contacto' : 'Nuevo Contacto'}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving} onClick={() => { const form = document.getElementById('contacto-form') as HTMLFormElement; form?.requestSubmit() }}>{saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear'}</button>
          </>
        }
      >
        <form id="contacto-form" onSubmit={handleSubmit}>
          {/* Section 1: Datos Personales */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem', marginBottom: '1rem', background: 'color-mix(in srgb, var(--rol-principal) 3%, transparent)' }}>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Datos del Contacto
            </h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Apellido</label>
                <input className="form-input" value={form.apellido} onChange={e => setForm(f => ({ ...f, apellido: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Cargo</label>
                <input className="form-input" value={form.cargo} onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))} placeholder="Ej: CTO, Gerente Comercial" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notas</label>
              <textarea className="form-textarea" value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} placeholder="Notas internas sobre este contacto..." rows={2} />
            </div>
          </div>

          {/* Section 2: Empresas Afiliadas */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h4 style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                Empresas Afiliadas ({afiliaciones.filter(a => a.cliente_id !== null).length})
              </h4>
              <button type="button" className="btn btn-sm btn-secondary" onClick={addAfil} style={{ fontSize: '0.8rem' }}>+ Agregar empresa</button>
            </div>

            {afiliaciones.map((af, idx) => (
              <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '0.75rem', marginBottom: idx < afiliaciones.length - 1 ? '0.75rem' : 0, background: 'var(--bg-secondary)', position: 'relative' }}>
                {afiliaciones.length > 1 && (
                  <button type="button" onClick={() => removeAfil(idx)} style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }} title="Quitar empresa">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                  </button>
                )}
                <div className="form-row" style={{ marginBottom: 8 }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Empresa</label>
                    <select className="form-select" value={af.cliente_id ?? ''} onChange={e => updateAfil(idx, 'cliente_id', e.target.value ? parseInt(e.target.value) : null)}>
                      <option value="">— Seleccionar empresa —</option>
                      {clientes
                        .filter(c => c.id === af.cliente_id || !afiliaciones.some(a => a.cliente_id === c.id))
                        .map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Cargo en esta empresa</label>
                    <input className="form-input" value={af.cargo_empresa} onChange={e => updateAfil(idx, 'cargo_empresa', e.target.value)} placeholder="Ej: Director Técnico" />
                  </div>
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.78rem' }}>Roles *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ROLES_CONTACTO.map(role => {
                      const active = af.roles.includes(role)
                      return (
                        <button key={role} type="button" onClick={() => toggleRole(idx, role)} style={{
                          padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: '1px solid',
                          background: active ? rolBadgeColors[role] : 'transparent',
                          color: active ? 'var(--text-on-primary)' : 'var(--text-secondary)',
                          borderColor: active ? rolBadgeColors[role] : 'var(--border)',
                          transition: 'all 0.15s ease',
                        }}>{role}</button>
                      )
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </form>
      </Modal>



      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirmar Eliminación"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancelar</button>
            <button className="btn btn-danger" onClick={handleDelete}>Eliminar</button>
          </>
        }
      >
        <p style={{ marginBottom: 8 }}>¿Estás seguro de eliminar al contacto <strong>{deleteConfirm?.nombre} {deleteConfirm?.apellido ?? ''}</strong>?</p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Se eliminarán también todas sus afiliaciones a empresas.</p>
      </Modal>

      {/* Credenciales del acceso recien creado */}
      <Modal
        isOpen={!!nuevoAcceso}
        onClose={() => setNuevoAcceso(null)}
        title="Acceso al portal creado"
        footer={<button className="btn btn-primary" onClick={() => setNuevoAcceso(null)}>Entendido</button>}
      >
        <p style={{ marginBottom: 12 }}>
          Comparte estas credenciales con el contacto. <strong>La contraseña no se vuelve a mostrar.</strong>
        </p>
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="form-group">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>Email</label>
            <input className="form-input" readOnly value={nuevoAcceso?.email ?? ''} onFocus={e => e.currentTarget.select()} />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ fontSize: '0.78rem' }}>Contraseña temporal</label>
            <input
              className="form-input"
              readOnly
              value={nuevoAcceso?.password ?? ''}
              onFocus={e => e.currentTarget.select()}
              style={{ fontFamily: 'monospace' }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}
